import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { COMPANY_DETAILS } from "../pages/appsettings";

// Theme colors (matches app brand green #2d5a3d)
const THEME_GREEN = [45, 90, 61];
const DARK_GREEN = [26, 58, 39];

const fmt = (val) =>
  Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/mainlogo.png";
  });

// Formats a value that may be a plain date/datetime string OR a unix timestamp.
const formatDate = (val, pattern = "YYYY-MM-DD") => {
  if (!val) return "-";
  const numeric = Number(val);
  if (String(val).trim() !== "" && !isNaN(numeric)) {
    return dayjs.unix(numeric).format(pattern);
  }
  return dayjs(val).isValid() ? dayjs(val).format(pattern) : "-";
};

// Shared branded letterhead used by the issue note document.
const renderLetterhead = async (doc, docTitle, docRef, pageWidth, margin) => {
  const logoX = margin;
  const logoY = 13;
  const logoSize = 22;

  const img = await loadLogo();
  if (img) {
    doc.addImage(img, "PNG", logoX, logoY, logoSize, logoSize);
  } else {
    doc.setFillColor(...THEME_GREEN);
    doc.roundedRect(logoX, logoY, logoSize, logoSize, 2, 2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("AF", logoX + logoSize / 2, logoY + logoSize / 2 + 1, { align: "center" });
  }

  const textX = logoX + logoSize + 7;
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text(COMPANY_DETAILS.name.toUpperCase(), textX, 18);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY_DETAILS.address, textX, 24);
  doc.text(`Tel: ${COMPANY_DETAILS.phone} | Mobile: ${COMPANY_DETAILS.mobile}`, textX, 29);
  doc.text(`Email: ${COMPANY_DETAILS.email} | Web: ${COMPANY_DETAILS.website}`, textX, 34);

  // ----- Document title (right aligned) -----
  doc.setFontSize(17);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text(docTitle, pageWidth - margin, 19, { align: "right" });

  doc.setFontSize(11);
  doc.setTextColor(...DARK_GREEN);
  doc.text(docRef || "", pageWidth - margin, 26, { align: "right" });

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(140, 140, 140);
  doc.text(`BR No: ${COMPANY_DETAILS.brNo}`, pageWidth - margin, 31, { align: "right" });

  // ----- Divider -----
  doc.setDrawColor(...THEME_GREEN);
  doc.setLineWidth(0.9);
  doc.line(margin, 39, pageWidth - margin, 39);
};

// Shared info section (two columns) + summary bar.
const renderInfoAndSummary = (doc, infoRows, items, totalQty, pageWidth, margin) => {
  const colWidth = (pageWidth - margin * 2) / 2;
  const half = Math.ceil(infoRows.length / 2);

  infoRows.forEach((row, i) => {
    const x = i < half ? margin : margin + colWidth;
    const y = 44.5 + (i % half) * 6.4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 100, 100);
    doc.text(`${row[0]}:`, x, y);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 30);
    doc.text(String(row[1]), x + 32, y);
  });

  let y = 44.5 + half * 6.4;

  // ----- Summary bar -----
  y += 3;
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(`Items: ${items.length}`, margin, y);
  doc.text(`Total Quantity: ${fmt(totalQty)}`, pageWidth / 2, y, { align: "center" });
  doc.text("Currency: Sri Lankan Rupee (LKR)", pageWidth - margin, y, { align: "right" });
  return y + 4;
};

// Shared signatures + footer.
// sigBlocks: [{ label: "Prepared By", name: "Saman Perera" }, ...] — rendered as
// exactly two signature blocks (Prepared By / Approved By), each showing the
// responsible person's name underneath the signature line.
const renderSignaturesAndFooter = (doc, sigBlocks, pageWidth, margin, startY) => {
  const pageHeight = doc.internal.pageSize.getHeight();

  // Reserve ~32mm from the bottom so the signature line + role label + name +
  // date/time (drawn down to sigY + ~17) never collide with the footer text.
  let sigY = Math.max(startY + 24, pageHeight - 42);
  if (sigY > pageHeight - 38) {
    doc.addPage();
    sigY = 40;
  }

  const sigColWidth = (pageWidth - margin * 2) / sigBlocks.length;
  sigBlocks.forEach((block, i) => {
    const cx = margin + sigColWidth * i + sigColWidth / 2;
    doc.setDrawColor(40, 40, 40);
    doc.setLineWidth(0.3);
    doc.line(cx - 20, sigY, cx + 20, sigY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 90, 90);
    doc.text(block.label, cx, sigY + 4.5, { align: "center" });

    if (block.name && block.name !== "-") {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(30, 30, 30);
      const nameLines = doc.splitTextToSize(String(block.name), 60);
      doc.text(nameLines, cx, sigY + 9, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(120, 120, 120);
      doc.text(String(block.date || "-"), cx, sigY + 9 + nameLines.length * 4.2 + 1.5, { align: "center" });
    }
  });

  // ----- Footer -----
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.text(
    "This is a computer-generated document. No signature is required.",
    pageWidth / 2,
    pageHeight - 9,
    { align: "center" }
  );
  doc.text(
    `Generated on ${dayjs().format("YYYY-MM-DD HH:mm:ss")} | AgriField Management System`,
    pageWidth / 2,
    pageHeight - 5.5,
    { align: "center" }
  );
};

/**
 * Builds a branded, A4 Material Issue Note PDF using jsPDF + autoTable.
 * @param {Object} data - Response of issuing/list.php?iin_id= (dataset with header + items)
 * @returns {Promise<jsPDF>} The generated jsPDF document (not yet saved)
 */
export const buildIssueNotePDF = async (data) => {
  const master = data || {};
  const items = data?.items || [];

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;

  await renderLetterhead(doc, "MATERIAL ISSUE NOTE", master.iin_ref || "", pageWidth, margin);

  const infoRows = [
    ["Issue Note Ref", master.iin_ref || "-"],
    ["MR Ref", master.mr_ref || "-"],
    ["Status", master.iin_status || "-"],
    ["Prepared By", master.prepare_user_dname || master.prepare_user || "-"],
    ["Prepared Date", formatDate(master.prepare_dt, "YYYY-MM-DD hh:mm A")],
    ["Approved By", master.complete_user_dname || master.complete_user || "-"],
    ["Approved Date", formatDate(master.completed_dt, "YYYY-MM-DD hh:mm A")],
    ["Collected By", master.collect_user_dname || master.collect_user || "-"],
    ["Collected Date", formatDate(master.collected_dt, "YYYY-MM-DD hh:mm A")],
  ];

  const totalQty = items.reduce((sum, it) => sum + (parseFloat(it.issuenote_qty) || 0), 0);
  let y = renderInfoAndSummary(doc, infoRows, items, totalQty, pageWidth, margin);

  // ----- Items table -----
  const bodyRows = items.map((it, idx) => [
    idx + 1,
    it.in_code || "-",
    it.in_name || "-",
    it.uom_name || "-",
    fmt(it.issuenote_qty),
    it.serial_numbers || "-",
    it.special_details || "-",
  ]);

  if (bodyRows.length === 0) {
    bodyRows.push([
      {
        content: "No items in this Material Issue Note.",
        colSpan: 7,
        styles: { halign: "center", fontStyle: "italic", textColor: [150, 150, 150] },
      },
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [["#", "Item Code", "Item Name", "UOM", "Issue Qty", "Serial Numbers", "Special Details"]],
    body: bodyRows,
    theme: "grid",
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: THEME_GREEN,
      textColor: 255,
      fontSize: 8.5,
      halign: "center",
      cellPadding: 2.5,
    },
    styles: {
      fontSize: 8,
      cellPadding: 2.2,
      overflow: "linebreak",
      textColor: [40, 40, 40],
      lineColor: [217, 217, 217],
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { cellWidth: 9, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 15 },
      4: { cellWidth: 18, halign: "right", fontStyle: "bold" },
      5: { cellWidth: 34 },
      6: { cellWidth: 36 },
    },
    didParseCell: (d) => {
      if (d.section === "body" && d.row.index % 2 === 1) {
        d.cell.styles.fillColor = [249, 250, 251];
      }
    },
  });

  renderSignaturesAndFooter(
    doc,
    [
      {
        label: "Prepared By",
        name: master.prepare_user_dname || master.prepare_user || "-",
        date: formatDate(master.prepare_dt, "YYYY/MM/DD hh:mm:ss A"),
      },
      {
        label: "Approved By",
        name: master.complete_user_dname || master.complete_user || "-",
        date: formatDate(master.completed_dt, "YYYY/MM/DD hh:mm:ss A"),
      },
    ],
    pageWidth,
    margin,
    doc.lastAutoTable.finalY + 6
  );

  return doc;
};

/**
 * Generates and downloads a Material Issue Note PDF directly.
 */
export const downloadIssueNotePDF = async (data) => {
  const doc = await buildIssueNotePDF(data);
  const ref = data?.iin_ref || "IIN";
  doc.save(`Material_Issue_Note_${ref}.pdf`);
  return doc;
};
